import React from 'react';
import styled, {css} from 'styled-components';
import {Detail, RichTextEditor, Toggle} from '@forecast-it/design-system';
import type {MergeField} from '../config/mergeFields';
import type {PlateValue} from '../resolver/types';

/**
 * The panel primitives, built to the design's own contract so every panel reads the same:
 *
 * - a panel is a stack of subsections, each separated from the last by a rule and 16px of space;
 * - a subsection puts 12px between its label and its content;
 * - a group label is subheading-2 in the main ink — not a small uppercase caption;
 * - a setting is a row with its label (body-2) and optional description (detail-1, auxiliary) on
 *   the left and its control on the right.
 *
 * The three type tokens are read off the theme rather than restated as pixel sizes, so they follow
 * the design system rather than drifting from it.
 */

/**
 * A panel's subsections.
 *
 * `$ruled` separates them with a rule, which the design uses only where the subsections would
 * otherwise run together — Estimate and Export. Panels whose rows already read as their own blocks
 * (Document Sections, Branding, Layout & Theme) are spaced apart and left unruled.
 */
export const Sections = styled.div<{$gap?: number; $ruled?: boolean}>`
	display: flex;
	flex-direction: column;
	gap: ${p => p.$gap ?? 24}px;
	padding: 4px 0 12px;

	${p =>
		p.$ruled
			? css`
					> * + * {
						border-top: 1px solid ${p.theme.sys.color.border.decorative};
						padding-top: 16px;
					}
			  `
			: ''}
`;

/** One subsection: its label, then its content. */
export const Section = styled.div`
	display: flex;
	flex-direction: column;
	row-gap: 12px;
`;

export const FieldRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

export const Label = styled.div`
	${({theme}) => theme.sys.font.body[2]}
`;

export const GroupLabel = styled.div`
	${({theme}) => theme.sys.font.subheading[2]}
	color: ${({theme}) => theme.sys.color.text.content.main};
`;

/** A setting: label and description on the left, control on the right. */
export const SettingRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
`;

const RowText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

const RowLabel = styled.span<{$heading?: boolean}>`
	display: flex;
	align-items: center;
	gap: 6px;
	${p => (p.$heading ? p.theme.sys.font.subheading[2] : p.theme.sys.font.body[2])}
	color: ${p => (p.$heading ? p.theme.sys.color.text.content.main : 'inherit')};
`;

const RowDescription = styled.span`
	${({theme}) => theme.sys.font.detail[1]}
	color: ${({theme}) => theme.sys.color.text.content.auxiliary};
	text-wrap: pretty;
`;

/** A stack of settings inside one subsection. */
export const SettingStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 14px;
`;

const NoteRow = styled.div`
	margin-top: 4px;
`;

export const SysFieldRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 7px 0;
	border-bottom: 1px solid ${({theme}) => theme.sys.color.border.decorative};

	&:last-child {
		border-bottom: 0;
	}
`;

export const SysFieldValue = styled.span`
	${({theme}) => theme.sys.font.subheading[2]}
`;

export const LabelledToggle = ({
	label,
	description,
	checked,
	disabled,
	cy,
	onChange,
	trailing,
	headingLabel,
}: {
	label: string;
	/** What the setting does, when the label alone does not say it. */
	description?: string;
	checked: boolean;
	disabled?: boolean;
	cy?: string;
	onChange: (checked: boolean) => void;
	/** Rendered beside the label — a lock, where the setting is owned elsewhere. */
	trailing?: React.ReactNode;
	/** True where the setting titles its own subsection, so its label takes the heading voice. */
	headingLabel?: boolean;
}): JSX.Element => (
	<SettingRow>
		<RowText>
			<RowLabel $heading={headingLabel}>
				{label}
				{trailing}
			</RowLabel>
			{description ? <RowDescription>{description}</RowDescription> : null}
		</RowText>
		<Toggle checked={checked} onChange={onChange} disabled={disabled} aria-label={label} cy={cy} />
	</SettingRow>
);

export const Note = ({children}: {children: React.ReactNode}): JSX.Element => (
	<NoteRow>
		<Detail color="auxiliary">{children}</Detail>
	</NoteRow>
);

export const NarrativeEditor = ({
	value,
	disabled,
	placeholder,
	mergeFields,
	onChange,
	cy,
}: {
	value: PlateValue;
	disabled: boolean;
	placeholder: string;
	mergeFields: MergeField[];
	onChange: (value: PlateValue) => void;
	cy?: string;
}): JSX.Element => (
	<RichTextEditor
		data-cy={cy}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the DS editor types its value as its own Plate AST; ours is the persisted structural twin
		initialValue={value as any}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same DS Plate AST bridge as initialValue
		onChange={(next: any) => onChange(next as PlateValue)}
		labelMentions={mergeFields.map(field => ({id: field.id, text: field.label}))}
		minHeight="120px"
		maxHeight="260px"
		variant="default"
		placeholder={placeholder}
		disabled={disabled}
	/>
);
